import React, { type ReactElement, useCallback } from "react";
import { graphql, useQueryLoader } from "react-relay";
import { ServerSearchListbox } from "@olivahealth/oli-ui";
import { debounce } from "lodash";
import { UserSelectorQuery as UserSelectorQueryType } from "./__generated__/UserSelectorQuery.graphql";

const UserSelectorQuery = graphql`
  query UserSelectorQuery($search: String, $includeOlivans: Boolean) {
    users(first: 20, search: $search, includeOlivans: $includeOlivans) {
      nodes {
        id
        name
        surname
        email
        status
        role
      }
    }
  }
`;

interface UserOption {
  id: string;
  email: string;
  name: string;
}

interface ExtendedOption {
  id: string;
  value: string;
  label: string;
  data?: UserOption;
}

interface Props {
  onUserSelected: (user: UserOption) => void;
  placeholder?: string;
  includeOlivans?: boolean;
}

export default function UserSelector({
  onUserSelected,
  placeholder,
  includeOlivans,
}: Props): ReactElement {
  const [usersQueryReference, loadUsersQuery] =
    useQueryLoader<UserSelectorQueryType>(UserSelectorQuery);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadUsersQuery = useCallback(debounce(loadUsersQuery, 500), [
    loadUsersQuery,
  ]);
  return (
    <ServerSearchListbox
      query={UserSelectorQuery}
      queryReference={usersQueryReference}
      transformDataToOptions={(data) => {
        const typedData = data as UserSelectorQueryType["response"];
        const users = typedData?.users?.nodes;
        if (!users) return [];

        // Store user data in the option for later retrieval
        return users.map((user) => ({
          id: user.id,
          value: user.id,
          label: `
            ${user.status === "SUSPENDED" ? "[SUSPENDED] " : ""}
            ${[user.name, user.surname].filter(Boolean).join(" ")} (${user.email})<br />${user.id} / ${user.role}`,
          data: {
            id: user.id,
            email: user.email,
            name: [user.name, user.surname].filter(Boolean).join(" "),
          },
        })) as ExtendedOption[];
      }}
      onSearchChange={(inputText) => {
        if (!inputText) {
          return;
        }
        debouncedLoadUsersQuery({
          search: inputText,
          includeOlivans,
        });
      }}
      setSelected={(option) => {
        const extendedOption = option as ExtendedOption;
        if (!extendedOption?.value || !extendedOption.data) return;
        onUserSelected(extendedOption.data);
      }}
      placeholder={placeholder ?? "Search by id, name, email"}
    />
  );
}
